import { Link } from "react-router-dom";
import { useGetMyRefereeRequestsQuery } from "../api/probationerApplicationApi";
import { useGetMyUpgradeRefereeRequestsQuery } from "../../upgrade-application/api/upgradeApi";
import { UserCheck, ArrowUpCircle, ChevronRight, Clock } from "lucide-react";

function pendingCount(rows: any[] | undefined) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter((r) => String(r?.decision ?? "pending").toLowerCase() === "pending").length;
}

function ReferralCard({
  to,
  title,
  subtitle,
  icon,
  pending,
  accent,
}: {
  to: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  pending: number;
  accent: string;
}) {
  return (
    <Link
      to={to}
      className="group relative flex flex-col justify-between bg-white border border-gray-200 rounded-[32px] p-8 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
    >
      <div className="flex items-start justify-between">
        <div className={`w-14 h-14 rounded-2xl grid place-items-center ${accent}`}>{icon}</div>
        {pending > 0 && (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-xs font-black">
            <Clock className="w-3.5 h-3.5" /> {pending} pending
          </span>
        )}
      </div>
      <div className="mt-6">
        <h3 className="text-xl font-black text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 font-medium mt-1">{subtitle}</p>
      </div>
      <div className="mt-6 flex items-center gap-1 text-sm font-bold text-gray-900">
        Review requests
        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

export default function ReferralsPage() {
  const { data: probRows } = useGetMyRefereeRequestsQuery();
  const { data: upgradeRows } = useGetMyUpgradeRefereeRequestsQuery();

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      <div className="space-y-1">
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Referrals</h1>
        <p className="text-sm text-gray-500 font-medium">
          Review and approve applications where you have been nominated as a referee or
          sponsor/mentor.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <ReferralCard
          to="/my-referee-request"
          title="Application Referrals"
          subtitle="Referee requests from membership (probationer) applications."
          icon={<UserCheck className="w-7 h-7 text-blue-600" />}
          accent="bg-blue-50"
          pending={pendingCount(probRows as any)}
        />
        <ReferralCard
          to="/my-upgrade-referee-request"
          title="Membership Upgrade Referrals"
          subtitle="Sponsor/mentor requests from membership grade-upgrade applications."
          icon={<ArrowUpCircle className="w-7 h-7 text-indigo-600" />}
          accent="bg-indigo-50"
          pending={pendingCount(upgradeRows as any)}
        />
      </div>
    </div>
  );
}
